import React from 'react';
import {
    Input
} from 'shared/components/ui/input';
import {
    Switch
} from 'shared/components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    cn
} from 'shared/lib/utils';
import {
    StepCommonAdvanced, StepCommonInlineFields, StringChipsEditor,
    useProcedureStepIds
} from './_shared';
import {
    LlmStepZone, LlmLabeled, LlmStepCanvas, llmFocusInputClass
} from './llmStepLayout';

/**
 * InlineMemoryStep — kind:"memory".
 * Reads conversation memory; opcodes never write memory in V1.
 */
export default function InlineMemoryStep({
    step, onPatchStep, oelContext
}) {
    const turns = step.turns ?? 5;
    const maxTokens = step.max_tokens ?? 2000;
    const mode = step.mode || 'full';
    const format = step.format || 'block';
    const userKeys = Array.isArray(step.user_keys) ? step.user_keys : [];
    const assistantKeys = Array.isArray(step.assistant_keys) ? step.assistant_keys : [];
    const includeTs = step.include_ts !== false;
    const maxListItems = step.max_list_items ?? 3;

    const stepIdOptions = useProcedureStepIds(oelContext);
    const inputClass = cn('h-8 text-[12.5px]', llmFocusInputClass);

    const patch = (p) => onPatchStep({ ...step, ...p });

    return (
        <LlmStepCanvas>
            <LlmStepZone
                variant="read"
                title="Read scope"
                description="How much conversation history to load into this step's output."
            >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <LlmLabeled
                        label="Turns"
                        help="Recent conversation turns to look back (1–200). One turn is typically a user message plus the assistant reply. Default 5."
                    >
                        <Input
                            type="number"
                            min={1}
                            max={200}
                            value={turns}
                            aria-label="memory turns"
                            onChange={(e) => patch({
                                turns: e.target.value === '' ? undefined : Number(e.target.value)
                            })}
                            className={inputClass}
                        />
                    </LlmLabeled>
                    <LlmLabeled
                        label="Max tokens"
                        help="Approximate size limit for recalled text. Older content is trimmed to stay under this budget. Default 2000."
                    >
                        <Input
                            type="number"
                            value={maxTokens}
                            aria-label="memory max tokens"
                            onChange={(e) => patch({
                                max_tokens: e.target.value === '' ? undefined : Number(e.target.value)
                            })}
                            className={inputClass}
                        />
                    </LlmLabeled>
                    <LlmLabeled
                        label="Mode"
                        help="Full: both user and assistant messages. Selective: only messages whose conversation keys match the filters below."
                    >
                        <Select value={mode} onValueChange={(v) => patch({ mode: v })}>
                            <SelectTrigger className={inputClass} aria-label="memory mode">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="full">full — entire conversation</SelectItem>
                                <SelectItem value="selective">selective — filter by keys</SelectItem>
                            </SelectContent>
                        </Select>
                    </LlmLabeled>
                    <LlmLabeled
                        label="Format"
                        help="Block: structured sections per turn — best for LLM steps. Text: one flat string — simpler for logging or APIs."
                    >
                        <Select value={format} onValueChange={(v) => patch({ format: v })}>
                            <SelectTrigger className={inputClass} aria-label="memory format">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="block">block — structured</SelectItem>
                                <SelectItem value="text">text — flat</SelectItem>
                            </SelectContent>
                        </Select>
                    </LlmLabeled>
                </div>
            </LlmStepZone>

            {mode === 'selective' && (
                <LlmStepZone
                    variant="filters"
                    title="Selective filters"
                    description="Only recall messages tagged with these conversation keys — leave a list empty to include all on that side."
                >
                    <LlmLabeled
                        label="User keys"
                        help="Include user messages whose conversation key is in this list (e.g. order_status). Empty = all user messages in the turn window."
                    >
                        <StringChipsEditor
                            value={userKeys}
                            onChange={(v) => patch({ user_keys: v })}
                            placeholder="e.g. order_status"
                        />
                    </LlmLabeled>
                    <LlmLabeled
                        label="Assistant keys"
                        help="Include assistant messages whose conversation key is in this list. Empty = all assistant messages in the turn window."
                    >
                        <StringChipsEditor
                            value={assistantKeys}
                            onChange={(v) => patch({ assistant_keys: v })}
                            placeholder="e.g. order_status"
                        />
                    </LlmLabeled>
                </LlmStepZone>
            )}

            <LlmStepZone
                variant="output"
                title="Output shape"
                description="What lands on @step_id.output for downstream steps to read."
            >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <LlmLabeled
                        label="Include timestamps"
                        help="When on, each recalled message includes when it was sent. Off saves tokens."
                    >
                        <div className={cn(
                            'flex items-center gap-2 rounded-md border px-3 py-2',
                            'border-amber-500/15 bg-white',
                            includeTs && 'border-amber-500/30 bg-amber-500/[0.06]'
                        )}>
                            <Switch
                                checked={includeTs}
                                aria-label="memory include timestamps"
                                onCheckedChange={(v) => patch({ include_ts: v })}
                            />
                            <span className={cn(
                                'text-sm',
                                includeTs ? 'font-medium text-amber-900/85' : 'text-muted-foreground'
                            )}>
                                {includeTs ? 'Timestamps included' : 'Timestamps omitted'}
                            </span>
                        </div>
                    </LlmLabeled>
                    <LlmLabeled
                        label="Max list items"
                        help="If a turn stores multiple snippets per side, cap how many items are included per list. Default 3."
                    >
                        <Input
                            type="number"
                            min={1}
                            value={maxListItems}
                            aria-label="memory max list items"
                            onChange={(e) => patch({
                                max_list_items: e.target.value === '' ? undefined : Number(e.target.value)
                            })}
                            className={inputClass}
                        />
                    </LlmLabeled>
                </div>
            </LlmStepZone>

            <StepCommonInlineFields
                step={step}
                onPatchStep={onPatchStep}
                stepIdOptions={stepIdOptions}
            />

            <StepCommonAdvanced
                step={step}
                onPatchStep={onPatchStep}
                oelContext={oelContext}
            />
        </LlmStepCanvas>
    );
}
